import type { Ref } from 'react'
import { type Element, type Text } from 'slate'
import { twMerge } from 'tailwind-merge'
import { Button } from './Button'
import { Divider } from './Divider'
import { Select, SelectItem } from './Select'
import styles from './styles.module.css'

type InlineFormat = 'list-item' | 'mention'
type LegacyFormat = 'editable-void' | 'text' | 'title'
type InternalFormat = 'private-image'
export type Mark = keyof Omit<
  Text,
  'text' | 'mentionQuery' | 'glossaryTerm' | 'pendingUploads'
>
export type Block = Exclude<
  Element['type'],
  InlineFormat | LegacyFormat | InternalFormat
>
export type ObjectAnnotation = {
  name: Block | Mark
  options: Record<string, string>
}
// Union type for all toolbar items
type ToolbarItem =
  | Block
  | Block[]
  | Mark
  | 'divider'
  | 'undo'
  | 'redo'
  | ObjectAnnotation

// Toolbar configuration interface
export type ToolbarConfig = ToolbarItem[]

export function Toolbar({
  config,
  className,
  ref,
}: {
  config: ToolbarConfig
  ref: Ref<HTMLDivElement>
  className?: string
}) {
  const groups = config.reduce<ToolbarItem[][]>(
    (acc, item, i) => {
      acc[acc.length - 1]!.push(item)

      if (item === 'divider' && config[i + 1]) {
        acc.push([])
      }

      return acc
    },
    [[]],
  )

  return (
    <div className={styles.container}>
      <div ref={ref} className={twMerge(styles.toolbar, className)}>
        {groups.map((group, groupIndex) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: Dunno
          <div key={groupIndex} className={styles.group}>
            {group.map((item, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: Dunno
              <ToolbarItem key={`${groupIndex}-${index}`} item={item} />
            ))}
          </div>
        ))}
        <div className={styles['group-xs']}>
          {config.map((item, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: Dunno
            <ToolbarItem key={`xs-${index}`} item={item} />
          ))}
        </div>
      </div>
    </div>
  )
}

export function ToolbarItem({ item }: { item: ToolbarItem }) {
  if (item === 'divider') {
    return <Divider />
  } else if (Array.isArray(item)) {
    return (
      <Select>
        {item.map((option) => (
          <SelectItem key={option} format={option} />
        ))}
      </Select>
    )
  } else if (typeof item === 'object' && 'name' in item) {
    return <Button action={item.name} options={item.options} />
  } else {
    return <Button action={item} />
  }
}
